import { Box, Typography } from "@mui/material";
import { boardTokens, OFF_WHITE, DARK_SURFACES } from "@/shared/theme";
import { formatTimeSpent } from "@/shared/utils/chess";
import { BoardPlayerAvatar } from "./BoardPlayerAvatar";
import { CapturedPieces } from "./review/CapturedPieces";

export const BoardPlayerRow = ({
  username,
  avatar,
  rating,
  clock,
  side,
  fen,
  color,
  profileUrl,
  avatarUsername,
}: {
  username: string;
  avatar?: string | null;
  rating: number | null;
  clock: { remaining: number | null; spent: number | null };
  side: "player" | "opponent";
  fen: string;
  color: "w" | "b";
  profileUrl?: string | null;
  avatarUsername?: string;
}) => (
  <Box
    data-testid={`board-${side}-row`}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      height: (theme) => theme.spacing(7),
      pl: {
        xs: `${boardTokens.evaluation.mobileGutterWidth}px`,
        sm: `${boardTokens.evaluation.gutterWidth}px`,
      },
      minWidth: 0,
      fontSize: "body2.fontSize",
      flexShrink: 0,
    }}
  >
    <Box
      data-avatar-username={avatarUsername ?? (profileUrl === null ? "" : username)}
      sx={{ fontSize: (theme) => theme.spacing(5), display: "flex", flexShrink: 0 }}
    >
      <BoardPlayerAvatar username={avatarUsername ?? (profileUrl === null ? "" : username)} src={avatar ?? undefined} />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box
        component={profileUrl === null ? "div" : "a"}
        href={
          profileUrl === null
            ? undefined
            : (profileUrl ?? (username ? `https://www.chess.com/member/${encodeURIComponent(username)}` : undefined))
        }
        target={profileUrl === null ? undefined : "_blank"}
        rel={profileUrl === null ? undefined : "noopener noreferrer"}
        title={username}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          minWidth: 0,
          color: "text.primary",
          textDecoration: "none",
          "&:hover": { color: "secondary.main" },
        }}
      >
        <Typography component="span" noWrap sx={{ fontSize: "inherit", fontWeight: "fontWeightBold" }}>
          {username || (side === "player" ? "You" : "Opponent")}
        </Typography>
        <Typography component="span" sx={{ fontSize: "inherit", color: "text.secondary", flexShrink: 0 }}>
          {rating ?? "—"}
        </Typography>
      </Box>
      <Box
        aria-label={`${username || (side === "player" ? "Your" : "Opponent")} lost pieces`}
        sx={{ overflow: "hidden" }}
      >
        <CapturedPieces fen={fen} color={color} />
      </Box>
    </Box>
    <Box
      sx={{
        ml: "auto",
        display: "flex",
        alignItems: "center",
        height: (theme) => theme.spacing(5),
        px: 1,
        bgcolor: OFF_WHITE.main,
        color: DARK_SURFACES.canvas,
        fontSize: "h5.fontSize",
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
        flexShrink: 0,
      }}
    >
      <Box
        component="span"
        title="Recorded clock remaining"
        data-testid="remaining-clock"
        sx={{ fontWeight: "fontWeightBold" }}
      >
        {clock.remaining == null ? "—" : formatTimeSpent(clock.remaining)}
      </Box>
    </Box>
  </Box>
);
